/**
 * RequiredFieldsSignal
 *
 * Chip de SINALIZAÇÃO (não bloqueia nada) para o header do ChatWindow dos 2
 * inboxes. Mostra quando o contato tem campos personalizados obrigatórios
 * PENDENTES na etapa/funil atual (ex.: Captação/Avaliação exige Ano e Modelo).
 * Clicar abre os dados do contato, onde os campos são preenchidos.
 *
 * Some quando não há obrigatórios pendentes. Reavalia quando `refreshKey` muda
 * (ex.: contato mudou de etiqueta ou salvou um campo).
 *
 * Props:
 *   - contactPhone
 *   - colors
 *   - refreshKey   -> muda para forçar refetch (opcional)
 *   - onClick      -> abrir dados do contato (opcional)
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import customFieldsService from '../../services/global/customFieldsService';
import requestCache from '../../services/global/requestCache';

const RequiredFieldsSignal = ({ contactPhone, colors, refreshKey, onClick }) => {
  const [missing, setMissing] = useState([]);

  useEffect(() => {
    let alive = true;
    if (!contactPhone) { setMissing([]); return undefined; }
    (async () => {
      try {
        // Cache curto por telefone + dedup em voo: o chip remonta/refaz a cada
        // re-render do ChatWindow (refreshKey = contagem de labels) — em log
        // real eram ~10 GETs /required POR ABERTURA de conversa. A mudança de
        // etiqueta troca o refreshKey, que fura o cache (key inclui ele).
        const status = await requestCache.cachedGet(
          `requiredStatus:${contactPhone}:${refreshKey ?? ''}`,
          () => customFieldsService.getContactRequiredStatus(contactPhone),
          { ttl: 30_000 },
        );
        if (alive) setMissing(status.missing || []);
      } catch (err) {
        if (alive) setMissing([]);
      }
    })();
    return () => { alive = false; };
  }, [contactPhone, refreshKey]);

  if (!missing.length) return null;

  const names = missing.map((m) => m.name).join(', ');
  const label = missing.length === 1
    ? '1 campo obrigatório'
    : `${missing.length} campos obrigatórios`;

  return (
    <button
      type="button"
      onClick={onClick}
      title={`Pendente nesta etapa: ${names}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '5px 10px', borderRadius: 16, cursor: onClick ? 'pointer' : 'default',
        border: '1px solid #F59E0B', backgroundColor: '#F59E0B1A',
        color: '#B45309', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <AlertTriangle size={13} />
      {label}
    </button>
  );
};

export default RequiredFieldsSignal;
